/**
 * What this device was paired as — its venue, its id, its key, its secret.
 *
 * Claimed once with a code from the office and kept on the device; nothing of
 * it is in the build. Stored where the device keeps its other durable things,
 * and read as none unless all of it is there — half a pairing is a device that
 * signs events for one venue with another's key.
 */

export interface DevicePairing {
  readonly apiKey: string;
  readonly tenantId: string;
  readonly venueId: string;
  readonly deviceId: string;
  readonly deviceSecret: string;
  readonly kind: "till" | "kitchen";
  readonly stationId: string | null;
  readonly name: string;
}

export type PairingFailure = "REFUSED" | "THROTTLED" | "UNREACHABLE";

export class PairingError extends Error {
  readonly reason: PairingFailure;
  constructor(reason: PairingFailure) {
    super(reason);
    this.name = "PairingError";
    this.reason = reason;
  }
}

/** Per kind: a till and a kitchen screen served from one origin share storage. */
const keyFor = (kind: DevicePairing["kind"]): string => `gf.device.${kind}`;
const TEXT = ["apiKey", "tenantId", "venueId", "deviceId", "deviceSecret", "name"] as const;

const valid = (raw: unknown): raw is DevicePairing => {
  if (!raw || typeof raw !== "object") return false;
  const p = raw as Record<string, unknown>;
  return (
    TEXT.every((k) => typeof p[k] === "string" && (p[k] as string).length > 0) &&
    (p["kind"] === "till" || p["kind"] === "kitchen") &&
    (p["stationId"] === null || typeof p["stationId"] === "string")
  );
};

export function readPairing(storage: Storage | null, kind: DevicePairing["kind"]): DevicePairing | null {
  try {
    const raw = storage?.getItem(keyFor(kind));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return valid(parsed) && parsed.kind === kind ? parsed : null;
  } catch {
    return null;
  }
}

export function savePairing(storage: Storage | null, pairing: DevicePairing): void {
  storage?.setItem(keyFor(pairing.kind), JSON.stringify(pairing));
}

/** Unpaired: the next start asks for a code again. */
export function forgetPairing(storage: Storage | null, kind: DevicePairing["kind"]): void {
  storage?.removeItem(keyFor(kind));
}

export async function claimPairing(args: {
  readonly baseUrl: string;
  readonly code: string;
  /** What this app is: a code for another kind is refused and not spent. */
  readonly kind?: "till" | "kitchen";
  readonly fetch?: typeof fetch;
}): Promise<DevicePairing> {
  let response: Response;
  try {
    response = await (args.fetch ?? fetch)(`${args.baseUrl}/pair/claim`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ code: args.code.replace(/\s/g, ""), ...(args.kind ? { kind: args.kind } : {}) }),
    });
  } catch {
    throw new PairingError("UNREACHABLE");
  }
  if (response.status === 429) throw new PairingError("THROTTLED");
  if (!response.ok) throw new PairingError("REFUSED");
  const body = (await response.json()) as unknown;
  if (!valid(body)) throw new PairingError("REFUSED");
  return body;
}

/**
 * The code in a pairing link (`#pair=123456`), or null. In the fragment so it
 * never reaches a server log; exactly six digits or nothing.
 */
export function pairingCodeFromHash(hash: string): string | null {
  const raw = new URLSearchParams(hash.replace(/^#/, "")).get("pair");
  const code = raw?.replace(/\s/g, "") ?? "";
  return /^\d{6}$/.test(code) ? code : null;
}
